import type { SalonNote, SalonNotesResponse, SalonRow } from '@manaro/contracts'

export type NotesByOwner = ReadonlyMap<number, readonly SalonNote[]>

/** The team's comments in the list: set when the back office has somewhere to keep them. */
export interface RowNotes {
  forRow: (row: SalonRow) => readonly SalonNote[]
  onOpen: (row: SalonRow) => void
}

/** Groups the newest-first list by owner, keeping the order inside each group. */
export function groupNotesByOwner(notes: readonly SalonNote[]): NotesByOwner {
  return notes.reduce((groups, note) => {
    groups.set(note.userId, [...(groups.get(note.userId) ?? []), note])
    return groups
  }, new Map<number, SalonNote[]>())
}

/**
 * Notes for one row: those written about this salon plus those about the owner before they had one,
 * so what the team wrote to a fresh registration stays in sight once the salon appears.
 */
export function rowNotes(byOwner: NotesByOwner, row: SalonRow): readonly SalonNote[] {
  const salonId = row.salon?.id ?? null
  return (byOwner.get(row.user.id) ?? []).filter((note) => note.salonId === null || note.salonId === salonId)
}

/** The salon the new note belongs to: the row's salon, or none for a registration without one. */
export const noteTarget = (row: SalonRow): { userId: number; salonId: number | null } => ({
  userId: row.user.id,
  salonId: row.salon?.id ?? null,
})

export const withNote = (data: SalonNotesResponse, note: SalonNote): SalonNotesResponse => ({
  ...data,
  notes: [note, ...data.notes.filter((existing) => existing.id !== note.id)],
})

export const replaceNote = (data: SalonNotesResponse, note: SalonNote): SalonNotesResponse => ({
  ...data,
  notes: data.notes.map((existing) => (existing.id === note.id ? note : existing)),
})

export const withoutNote = (data: SalonNotesResponse, id: string): SalonNotesResponse => ({
  ...data,
  notes: data.notes.filter((note) => note.id !== id),
})

export const isNoteAuthor = (note: SalonNote, email: string | undefined): boolean =>
  email !== undefined && note.authorEmail.toLowerCase() === email.toLowerCase()
